/* ==========================================================================
   INDIGO OSLO — filters.js
   Collection filtering + infinite scroll.

   Filtering stays on Shopify's native `filter.*` URL parameters, so every
   filtered view remains a real, shareable, server-rendered, indexable URL.
   This script only intercepts the navigation to swap the grid in place and
   animate the reflow with Flip.
   ========================================================================== */

(function () {
  'use strict';

  var form = document.querySelector('[data-filter-form]');
  var gridWrap = document.querySelector('[data-grid-wrap]');
  if (!gridWrap) return;

  var grid = gridWrap.querySelector('[data-grid]');
  var countEl = document.querySelector('[data-result-count]');
  var gsap = window.gsap;
  var Flip = window.Flip;
  var controller = null;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canFlip = !!(gsap && Flip) && !REDUCED;

  /* ----------------------------------------------------------------------
     Fetch a URL and swap in its grid
     ---------------------------------------------------------------------- */

  function load(url, push) {
    if (controller) controller.abort();
    controller = new AbortController();

    grid.classList.add('is-loading');

    // Capture the pre-change layout so Flip can interpolate to the new one
    var state = canFlip ? Flip.getState(grid.querySelectorAll('[data-product-card]')) : null;

    return fetch(url, { signal: controller.signal })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var newGrid = doc.querySelector('[data-grid]');
        var newCount = doc.querySelector('[data-result-count]');
        var newFilters = doc.querySelector('[data-filter-form]');
        var newPager = doc.querySelector('[data-load-more]');

        if (newGrid) {
          grid.innerHTML = newGrid.innerHTML;
        }
        if (newCount && countEl) {
          countEl.textContent = newCount.textContent;
        }
        if (newFilters && form) {
          form.innerHTML = newFilters.innerHTML;
        }

        var pager = document.querySelector('[data-load-more]');
        if (pager) {
          newPager ? pager.replaceWith(newPager) : pager.remove();
        } else if (newPager) {
          gridWrap.appendChild(newPager);
        }

        grid.classList.remove('is-loading');

        if (push) {
          window.history.pushState({ url: url }, '', url);
        }

        announce();

        if (canFlip && state) {
          Flip.from(state, {
            duration: 0.65,
            ease: 'power3.inOut',
            stagger: 0.012,
            absolute: true,
            onEnter: function (els) {
              return gsap.fromTo(
                els,
                { opacity: 0, scale: 0.94 },
                { opacity: 1, scale: 1, duration: 0.5, stagger: 0.02, ease: 'power3.out' }
              );
            },
            onLeave: function (els) {
              return gsap.to(els, { opacity: 0, scale: 0.94, duration: 0.3 });
            }
          });
        }

        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
        notifyAppended(grid.querySelectorAll('[data-product-card]'));
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        grid.classList.remove('is-loading');
        // Fall back to a real navigation rather than stranding the user
        window.location.href = url;
      });
  }

  function announce() {
    var region = document.querySelector('[data-filter-live]');
    if (region && countEl) {
      region.textContent = countEl.textContent;
    }
  }

  function notifyAppended(cards) {
    document.dispatchEvent(
      new CustomEvent('grid:appended', { detail: { cards: Array.prototype.slice.call(cards) } })
    );
  }

  /* ----------------------------------------------------------------------
     Filter form
     ---------------------------------------------------------------------- */

  if (form) {
    // Checkbox / radio changes
    form.addEventListener('change', function () {
      var data = new FormData(form);
      var params = new URLSearchParams();

      data.forEach(function (value, key) {
        if (value) params.append(key, value);
      });

      var url = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      load(url, true);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
    });
  }

  // Remove-a-filter chips and sort links anywhere on the page
  document.addEventListener('click', function (e) {
    var chip = e.target.closest('[data-filter-remove], [data-sort-link]');
    if (!chip) return;
    e.preventDefault();
    load(chip.getAttribute('href'), true);
  });

  // Sort <select>
  var sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', sortSelect.value);
      load(window.location.pathname + '?' + params.toString(), true);
    });
  }

  // Filter panel disclosure
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-filter-toggle]');
    if (!toggle) return;
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    var open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    panel.classList.toggle('is-open', !open);
  });

  window.addEventListener('popstate', function (e) {
    load((e.state && e.state.url) || window.location.href, false);
  });

  /* ----------------------------------------------------------------------
     Infinite scroll
     Appends the next page when the sentinel comes into view. The real
     paginated links stay in the DOM as the no-JS path.
     ---------------------------------------------------------------------- */

  var loadingMore = false;

  function loadMore(button) {
    if (loadingMore) return;
    var next = button.dataset.nextUrl || button.getAttribute('href');
    if (!next) return;

    loadingMore = true;
    button.setAttribute('aria-busy', 'true');

    fetch(next)
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newCards = doc.querySelectorAll('[data-grid] [data-product-card]');
        var newPager = doc.querySelector('[data-load-more]');

        var added = [];
        newCards.forEach(function (c) {
          grid.appendChild(c);
          added.push(c);
        });

        if (gsap && !REDUCED) {
          gsap.from(added, {
            opacity: 0,
            y: 28,
            duration: 0.7,
            stagger: 0.03,
            ease: 'power3.out',
            clearProps: 'opacity,transform'
          });
        }

        var pager = document.querySelector('[data-load-more]');
        if (newPager) {
          pager.replaceWith(newPager);
          observeSentinel();
        } else if (pager) {
          pager.remove();
        }

        window.history.replaceState({ url: next }, '', next);
        notifyAppended(added);
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      })
      .catch(function () {
        button.removeAttribute('aria-busy');
      })
      .finally(function () {
        loadingMore = false;
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-load-more-btn]');
    if (!btn) return;
    e.preventDefault();
    loadMore(btn);
  });

  var sentinelObserver = null;

  function observeSentinel() {
    if (!('IntersectionObserver' in window)) return;
    if (sentinelObserver) sentinelObserver.disconnect();

    var btn = document.querySelector('[data-load-more-btn]');
    if (!btn) return;

    sentinelObserver = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) loadMore(btn);
      },
      { rootMargin: '600px 0px' }
    );
    sentinelObserver.observe(btn);
  }

  observeSentinel();
})();
